import React from 'react'
import styled from 'styled-components'

import { CatalogBrandProductsPanel } from 'features/catalog/components/CatalogBrandProductsPanel/CatalogBrandProductsPanel'
import { CatalogSourceDetailPanel } from 'features/catalog/components/CatalogSourceDetailPanel/CatalogSourceDetailPanel'
import ProductSelector from 'features/catalog/components/ProductSelector'
import ThemeSelector from 'features/catalog/components/ThemeSelector'
import type { FocusListOption } from 'features/catalog/types'
import { getCreateTimeRange } from 'features/filters/prior-date-range'
import type { FilterOption } from 'features/filters/types'
import type { Product } from 'features/products/types'
import { Stats } from 'features/summary/components/Stats'
import type { StatsComparisonResponse } from 'features/summary/service/types'
import type { DateWindows, Filters } from 'types/project'

export interface OverviewViewProps {
  item: FocusListOption
  subthemeName: string | null
  setSubthemeValue: (value: string | null) => void
  statsLoading: boolean
  setActiveValues: (v: string[] | ((prev: string[]) => string[])) => void
  isProduct: boolean
  isTheme: boolean
  isSource: boolean
  isBrand: boolean
  activeFilters: Filters
  projectId: string | null
  defaultFilterList: FilterOption[]
  hierachyProductsData?: Product[]
  statsComparison?: StatsComparisonResponse | undefined
  comparisonLoading?: boolean
  dateWindows?: DateWindows
}

const OverviewView: React.FC<OverviewViewProps> = ({
  item,
  subthemeName,
  setSubthemeValue,
  statsLoading,
  setActiveValues,
  isProduct,
  isTheme,
  isSource,
  isBrand,
  activeFilters,
  projectId,
  defaultFilterList,
  hierachyProductsData,
  statsComparison,
  comparisonLoading,
  dateWindows,
}) => {
  return (
    <OverviewContainer>
      <StatsWrapper>
        <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>
          Summary
        </h3>
        <Stats
          summaryRequest={null}
          dateRange={getCreateTimeRange(activeFilters)}
          filterList={defaultFilterList}
          omittedOptions={[
            'n_rating_only',
            'n_recommended_posts',
            'n_promo_posts',
            'n_themes_total',
            'n_themes_provided',
          ]}
          columns={5}
          loading={statsLoading}
          projectId={projectId ?? ''}
          statsComparison={statsComparison}
          comparisonLoading={comparisonLoading ?? false}
          dateWindows={dateWindows ?? ({} as DateWindows)}
          defaultFilterList={defaultFilterList}
        />
      </StatsWrapper>
      {isSource && (
        <FlushWidgetWrapper className={'source-detail'}>
          <SectionHeading>Products</SectionHeading>
          <CatalogSourceDetailPanel item={item} projectId={projectId ?? ''} />
        </FlushWidgetWrapper>
      )}
      {isBrand && (
        <FlushWidgetWrapper className={'brand-products'}>
          <SectionHeading>Products</SectionHeading>
          <CatalogBrandProductsPanel
            item={item}
            projectId={projectId ?? ''}
            defaultFilterList={defaultFilterList}
          />
        </FlushWidgetWrapper>
      )}
      <div>
        {isProduct && (
          <WidgetWrapper className={'product-selector'} style={{ padding: 0 }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>
              Listings
            </h3>
            <ProductSelector hierachyProductsData={hierachyProductsData} />
          </WidgetWrapper>
        )}
      </div>
      {isTheme && item?.identifiers?.length > 1 && (
        <WidgetWrapper className={'theme-selector'} style={{ padding: 0 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>
            Child themes
          </h3>
          <ThemeSelector
            setActiveValues={setActiveValues}
            options={item.identifiers}
            subthemeName={subthemeName}
            setSubthemeValue={setSubthemeValue}
            themeName={item.value}
          />
        </WidgetWrapper>
      )}
    </OverviewContainer>
  )
}

export default OverviewView

const OverviewContainer = styled.div`
  height: 100%;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
  overflow: auto;
`

const WidgetWrapper = styled.div`
  border-radius: var(--border-radius);
  padding: var(--default-padding-half);
  width: 100%;
`

const FlushWidgetWrapper = styled(WidgetWrapper)`
  padding: 0;
`

const SectionHeading = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
`

const StatsWrapper = styled.div`
  margin-top: 10px;
  width: 100%;
`
